import { appStorage } from './betaStorage'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApp } from '../store/AppContext'
import { loadRoutines } from './dailyLiving'
import { getOnboardingRecord, ONBOARDING_REQUIREMENTS } from './onboarding'
import { localISODate } from './date'
import type { Routine } from './dailyLiving'

export type NotificationKind = 'tag' | 'task' | 'routine' | 'shift' | 'cover' | 'onboarding'
export type NotificationUrgency = 'normal' | 'important' | 'urgent'

export interface AppNotification {
  id: string
  kind: NotificationKind
  urgency: NotificationUrgency
  icon: string
  title: string
  message: string
  path: string
  createdAt: string
  sourceId?: string
  canComplete?: boolean
}

interface NotificationState {
  readIds: string[]
  snoozedUntil: Record<string, string>
}

const STORAGE_PREFIX = 'schub.notifications.v1.'
const CHANGE_EVENT = 'schub-notifications-changed'

function storageKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}`
}

function loadState(userId: string): NotificationState {
  try {
    const raw = appStorage.getItem(storageKey(userId))
    if (!raw) return { readIds: [], snoozedUntil: {} }
    const parsed = JSON.parse(raw) as Partial<NotificationState>
    return {
      readIds: Array.isArray(parsed.readIds) ? parsed.readIds : [],
      snoozedUntil: parsed.snoozedUntil && typeof parsed.snoozedUntil === 'object' ? parsed.snoozedUntil : {},
    }
  } catch {
    return { readIds: [], snoozedUntil: {} }
  }
}

function saveState(userId: string, state: NotificationState) {
  appStorage.setItem(storageKey(userId), JSON.stringify(state))
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

function addDays(dateISO: string, days: number) {
  const date = new Date(`${dateISO}T00:00:00`)
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

function appliesToday(routine: Routine, today: Date) {
  const day = today.getDay()
  const short = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][day]
  if (routine.repeat === 'daily') return true
  if (routine.repeat === 'weekdays') return day >= 1 && day <= 5
  if (routine.repeat === 'weekly') return day === new Date(routine.createdAt).getDay()
  if (routine.repeat === 'selected_days') return routine.selectedDays.includes(short)
  return false
}

export function useNotifications() {
  const app = useApp()
  const { currentUser, tasks, updates, events, can, pendingCoverRequestsFor } = app
  const [revision, setRevision] = useState(0)
  const todayISO = localISODate()

  useEffect(() => {
    const refresh = () => setRevision((value) => value + 1)
    window.addEventListener(CHANGE_EVENT, refresh)
    window.addEventListener('storage', refresh)
    window.addEventListener('schub-onboarding-updated', refresh)
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh)
      window.removeEventListener('storage', refresh)
      window.removeEventListener('schub-onboarding-updated', refresh)
    }
  }, [])

  const notifications = useMemo<AppNotification[]>(() => {
    void revision
    const items: AppNotification[] = []
    const today = new Date(`${todayISO}T12:00:00`)
    const tomorrowISO = addDays(todayISO, 1)

    updates.forEach((update) => {
      if (!update.taggedUserIds?.includes(currentUser.id)) return
      const author = app.allUsers.find((user) => user.id === update.authorId)
      items.push({
        id: `tag-${update.id}-${currentUser.id}`,
        kind: 'tag',
        urgency: 'important',
        icon: '💬',
        title: `${author?.name ?? 'Someone'} tagged you`,
        message: update.text,
        path: '/updates',
        createdAt: update.createdAt,
        sourceId: update.id,
      })
    })

    tasks.forEach((task) => {
      if (task.assignedToUserId !== currentUser.id || task.status === 'done') return
      if (task.dueDate > tomorrowISO) return
      const overdue = task.dueDate < todayISO
      const dueToday = task.dueDate === todayISO
      items.push({
        id: `task-${task.id}`,
        kind: 'task',
        urgency: overdue || task.priority === 'high' ? 'urgent' : 'important',
        icon: '✅',
        title: overdue ? 'Task overdue' : dueToday ? 'Task due today' : 'Task due tomorrow',
        message: task.title,
        path: '/tasks',
        createdAt: task.createdAt,
        sourceId: task.id,
        canComplete: true,
      })
    })

    if (can('view_tasks') && ['participant', 'rep', 'support_worker'].includes(currentUser.role)) {
      loadRoutines().forEach((routine) => {
        if (!appliesToday(routine, today)) return
        const remaining = routine.steps.filter((step) => !step.done).length
        if (!remaining) return
        items.push({
          id: `routine-${routine.id}-${todayISO}`,
          kind: 'routine',
          urgency: 'normal',
          icon: '🔁',
          title: routine.title,
          message: `${remaining} step${remaining === 1 ? '' : 's'} left today`,
          path: '/routines',
          createdAt: `${todayISO}T08:00:00`,
          sourceId: routine.id,
        })
      })
    }

    events.forEach((event) => {
      const assignee = event.assignees.find((item) => item.userId === currentUser.id && item.status !== 'declined')
      if (assignee && (event.date === todayISO || event.date === tomorrowISO)) {
        items.push({
          id: `shift-${event.id}-${currentUser.id}-${event.date}`,
          kind: 'shift',
          urgency: assignee.status === 'pending' ? 'important' : 'normal',
          icon: event.type === 'shift' ? '🗓️' : '📅',
          title: assignee.status === 'pending' ? 'Shift needs your response' : event.date === todayISO ? 'Scheduled today' : 'Scheduled tomorrow',
          message: `${event.title} · ${event.startTime}–${event.endTime}`,
          path: '/calendar',
          createdAt: `${event.date}T${event.startTime}:00`,
          sourceId: event.id,
        })
      }

      if (event.cover?.status === 'needs_cover' && can('manage_cover') && event.date >= todayISO) {
        items.push({
          id: `needs-cover-${event.id}`,
          kind: 'cover',
          urgency: 'urgent',
          icon: '⚠️',
          title: 'Shift needs cover',
          message: `${event.title} · ${event.date} ${event.startTime}`,
          path: '/calendar',
          createdAt: event.cover.withdrawnAt,
          sourceId: event.id,
        })
      }
    })

    pendingCoverRequestsFor(currentUser.id).forEach((request) => {
      const event = events.find((item) => item.id === request.eventId)
      items.push({
        id: `cover-request-${request.id}`,
        kind: 'cover',
        urgency: 'urgent',
        icon: '🙋',
        title: 'Can you cover this shift?',
        message: event ? `${event.title} · ${event.date} ${event.startTime}` : 'Open Marnixia to review the cover request.',
        path: '/today',
        createdAt: request.createdAt,
        sourceId: request.id,
      })
    })

    if (currentUser.role === 'support_worker' && can('view_onboarding')) {
      const record = getOnboardingRecord(currentUser.id)
      const remaining = ONBOARDING_REQUIREMENTS.filter((item) => !record.completedItemIds.includes(item.id)).length
      if (remaining > 0) {
        items.push({
          id: `onboarding-${currentUser.id}-${remaining}`,
          kind: 'onboarding',
          urgency: 'normal',
          icon: '🎓',
          title: 'Onboarding to complete',
          message: `${remaining} item${remaining === 1 ? '' : 's'} remaining`,
          path: '/onboarding',
          createdAt: record.updatedAt,
        })
      }
    }

    return items.sort((a, b) => {
      const urgency = { urgent: 0, important: 1, normal: 2 }
      return urgency[a.urgency] - urgency[b.urgency] || b.createdAt.localeCompare(a.createdAt)
    })
  }, [revision, currentUser, tasks, updates, events, can, pendingCoverRequestsFor, app.allUsers, todayISO])

  const stored = loadState(currentUser.id)
  const now = Date.now()
  const visible = notifications.filter((item) => {
    const until = stored.snoozedUntil[item.id]
    return !until || new Date(until).getTime() <= now
  })
  const unread = visible.filter((item) => !stored.readIds.includes(item.id))

  const markRead = useCallback((id: string) => {
    const next = loadState(currentUser.id)
    if (!next.readIds.includes(id)) next.readIds.push(id)
    saveState(currentUser.id, next)
  }, [currentUser.id])

  const markAllRead = useCallback(() => {
    const next = loadState(currentUser.id)
    next.readIds = Array.from(new Set([...next.readIds, ...notifications.map((item) => item.id)]))
    saveState(currentUser.id, next)
  }, [currentUser.id, notifications])

  const snooze = useCallback((id: string, minutes = 60) => {
    const next = loadState(currentUser.id)
    next.snoozedUntil[id] = new Date(Date.now() + minutes * 60_000).toISOString()
    saveState(currentUser.id, next)
  }, [currentUser.id])

  return {
    notifications: visible,
    unreadCount: unread.length,
    isUnread: (id: string) => !stored.readIds.includes(id),
    markRead,
    markAllRead,
    snooze,
  }
}

